import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Box, Typography, Card, CardContent } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const HourlyPatternChart = ({ data, title = "Padrão de Vendas por Horário" }) => {
  // Verificar se dados existem e são válidos
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Card sx={{ height: 400 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Box display="flex" alignItems="center" justifyContent="center" height={300}>
            <Typography color="text.secondary">
              Não há dados disponíveis para exibir
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Ordenar dados por horário
  const sortedData = [...data].sort((a, b) => (a.hour || 0) * 60 + (a.minute || 0) - ((b.hour || 0) * 60 + (b.minute || 0)));
  
  const chartData = {
    labels: sortedData.map(item => item.time_label || `${item.hour || 0}:${String(item.minute || 0).padStart(2, '0')}`),
    datasets: [
      {
        label: 'Volume de Vendas',
        data: sortedData.map(item => item.total_sales || 0),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 1,
        yAxisID: 'y',
      },
      {
        label: 'Receita (R$)',
        data: sortedData.map(item => parseFloat(item.total_revenue || 0)),
        backgroundColor: 'rgba(255, 107, 107, 0.8)',
        borderColor: 'rgba(255, 107, 107, 1)',
        borderWidth: 1,
        yAxisID: 'y1',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset?.label || '';
            const value = context.parsed?.y || 0;
            
            if (label.includes('Receita')) {
              return `${label}: R$ ${value.toLocaleString('pt-BR', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}`;
            }
            return `${label}: ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Horário do Dia',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Volume de Vendas',
        },
        ticks: {
          callback: function(value) {
            return value.toLocaleString('pt-BR');
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Receita (R$)',
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: function(value) {
            return `R$ ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
    },
  };

  return (
    <Card sx={{ height: 400 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
        <Box height={320}>
          <Bar data={chartData} options={options} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Valores normalizados: média de vendas e receita por horário, considerando apenas os dias em que o horário esteve disponível.
        </Typography>
      </CardContent>
    </Card>
  );
};

export default HourlyPatternChart;
